import { Injectable, inject, untracked } from '@angular/core';
import { normalizeChatbotText } from './chatbot-normalize.util';
import { SessionDataService } from '../../services/shared/SessionDataService.service';
import { Client } from '../../interfaces/ClientGlobalInterface.type';
import { Entity } from '../../interfaces/EntityGlobalInterface.type';
import { Agrupation } from '../../interfaces/AgrupationGlobalInterface.type';

/** Located client match with its optional entity and agrupation context. */
export interface NavigationMatch {
  client: Client;
  entity: Entity | null;
  agrupation: Agrupation | null;
  /** Human-readable label for disambiguation prompts. */
  label: string;
}

/** Resolves clients, entities and agrupations named in chat text for context switches. */
@Injectable({ providedIn: 'root' })
export class ChatbotNavigationService {
  private readonly sessionDataService = inject(SessionDataService);

  // ANCHOR: Context Search

  /**
   * Searches the loaded client list for agrupations whose name or alias
   * contains the given query string (case-insensitive, diacritics stripped).
   * Returns all matching triples so the caller can handle disambiguation.
   */
  findAgrupationMatches(query: string): NavigationMatch[] {
    const clientList = untracked(() => this.sessionDataService.clientList()) as Client[] | null;
    if (!clientList || !query.trim()) { return []; }
    const norm = normalizeChatbotText(query);
    const matches: NavigationMatch[] = [];

    for (const client of clientList) {
      for (const entity of (client.entityList ?? [])) {
        for (const agrupation of (entity.agrupations ?? [])) {
          if (
            normalizeChatbotText(agrupation.name ?? '').includes(norm) ||
            normalizeChatbotText(agrupation.alias ?? '').includes(norm)
          ) {
            matches.push({
              client,
              entity,
              agrupation,
              label: this.buildLabel(client, entity, agrupation),
            });
          }
        }
      }
    }
    // Sort: exact name / alias matches appear first in disambiguation prompts.
    return matches.sort((a, b) => {
      const aN = normalizeChatbotText(a.agrupation?.name  ?? '');
      const aA = normalizeChatbotText(a.agrupation?.alias ?? '');
      const bN = normalizeChatbotText(b.agrupation?.name  ?? '');
      const bA = normalizeChatbotText(b.agrupation?.alias ?? '');
      const aExact = aN === norm || aA === norm;
      const bExact = bN === norm || bA === norm;
      if (aExact !== bExact) { return aExact ? -1 : 1; }
      return 0;
    });
  }

  /**
   * Searches for entities whose name or alias contains the given query.
   * Returns all matching client+entity pairs.
   */
  findEntityMatches(query: string): NavigationMatch[] {
    const clientList = untracked(() => this.sessionDataService.clientList()) as Client[] | null;
    if (!clientList || !query.trim()) { return []; }
    const norm = normalizeChatbotText(query);
    const matches: NavigationMatch[] = [];

    for (const client of clientList) {
      for (const entity of (client.entityList ?? [])) {
        if (
          normalizeChatbotText(entity.entity).includes(norm) ||
          normalizeChatbotText(entity.alias ?? '').includes(norm)
        ) {
          // Default to first agrupation when the entity itself is the target
          const agrupation = (entity.agrupations ?? [])[0] ?? null;
          matches.push({
            client,
            entity,
            agrupation,
            label: this.buildLabel(client, entity, null),
          });
        }
      }
    }
    // Sort: exact name / alias matches appear first in disambiguation prompts.
    return matches.sort((a, b) => {
      const aN = normalizeChatbotText(a.entity?.entity ?? '');
      const aA = normalizeChatbotText(a.entity?.alias  ?? '');
      const bN = normalizeChatbotText(b.entity?.entity ?? '');
      const bA = normalizeChatbotText(b.entity?.alias  ?? '');
      const aExact = aN === norm || aA === norm;
      const bExact = bN === norm || bA === norm;
      if (aExact !== bExact) { return aExact ? -1 : 1; }
      return 0;
    });
  }

  /**
   * Searches client names and ranks exact matches first.
   * Returns the first entity and first agrupation as defaults for each match.
   */
  findClientMatches(query: string): NavigationMatch[] {
    const clientList = untracked(() => this.sessionDataService.clientList()) as Client[] | null;
    if (!clientList || !query.trim()) { return []; }
    const norm = normalizeChatbotText(query);
    const matches: NavigationMatch[] = [];

    for (const client of clientList) {
      if (normalizeChatbotText(client.clientName ?? '').includes(norm)) {
        const firstEntity = (client.entityList ?? [])[0] ?? null;
        const firstAgrupation = firstEntity ? (firstEntity.agrupations ?? [])[0] ?? null : null;
        matches.push({
          client,
          entity: firstEntity,
          agrupation: firstAgrupation,
          label: client.clientName ?? '',
        });
      }
    }
    // Sort: exact name matches appear first in disambiguation prompts.
    return matches.sort((a, b) => {
      const aN = normalizeChatbotText(a.client?.clientName ?? '');
      const bN = normalizeChatbotText(b.client?.clientName ?? '');
      const aExact = aN === norm;
      const bExact = bN === norm;
      if (aExact !== bExact) { return aExact ? -1 : 1; }
      return 0;
    });
  }

  // ANCHOR: Session Update

  /**
   * Applies a NavigationMatch to the live session signals, updating the
   * top-bar dropdowns to reflect the selected client / entity / agrupation.
   * Null entity/agrupation (e.g. client-only match) are skipped.
   * Shallow-copies each object before sending so Angular's Object.is() equality
   * check always detects a reference change, even when the same logical
   * client/entity/agrupation is re-selected (e.g. switching only the agrupation).
   * Sending order (client → entity → agrupation) matches the cascade expected by
   * navbar-top effects: the last sendAgrupation always wins as the final state.
   */
  applyMatch(match: NavigationMatch): void {
    // Set flag BEFORE sending signals so the navbar's setCurrentClient /
    // setCurrentEntity auto-select cascade is suppressed while effects run.
    // Without this, setCurrentClient fires sendEntity(firstEntity) and
    // setCurrentEntity fires sendAgrupation(firstNonVirtual), both of which
    // would overwrite the chatbot's explicit entity + agrupation choices.
    this.sessionDataService.sendChatbotApplyingMatch(true);
    this.sessionDataService.sendClient({ ...match.client });
    if (match.entity) {
      this.sessionDataService.sendEntity({ ...match.entity });
    }
    if (match.agrupation) {
      this.sessionDataService.sendAgrupation({ ...match.agrupation });
    }
    // Reset after a macrotask tick — by then all synchronous effects have settled.
    setTimeout(() => this.sessionDataService.sendChatbotApplyingMatch(false), 0);
  }

  /** Builds a short human-readable label for disambiguation display. */
  private buildLabel(client: Client, entity: Entity | null, agrupation: Agrupation | null): string {
    const parts: string[] = [];
    if (client.clientName) { parts.push(client.clientName); }
    if (entity?.entity) { parts.push(entity.entity); }
    if (agrupation?.name) { parts.push(agrupation.name); }
    return parts.join(' › ');
  }
}
